import { useCallback, useEffect, useMemo, useState } from "react";
import { listCompanyEventRegistrations } from "../api/listCompanyEventRegistrations";
import type { CompanyEventRegistrationItem } from "../api/listCompanyEventRegistrations";

export type CompanyRegistrationGroups = {
  thisWeek: CompanyEventRegistrationItem[];
  laterThisMonth: CompanyEventRegistrationItem[];
  future: CompanyEventRegistrationItem[];
  past: CompanyEventRegistrationItem[];
};

function groupByTimeHorizon(
  items: CompanyEventRegistrationItem[],
): CompanyRegistrationGroups {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const oneWeekOut = new Date(startOfToday);
  oneWeekOut.setDate(startOfToday.getDate() + 7);
  const endOfThisMonth = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
  );

  const thisWeek: CompanyEventRegistrationItem[] = [];
  const laterThisMonth: CompanyEventRegistrationItem[] = [];
  const future: CompanyEventRegistrationItem[] = [];
  const past: CompanyEventRegistrationItem[] = [];

  for (const r of items) {
    if (!r.startDate) {
      future.push(r);
      continue;
    }
    const d = new Date(r.startDate);
    if (d < startOfToday) {
      past.push(r);
    } else if (d < oneWeekOut) {
      thisWeek.push(r);
    } else if (d <= endOfThisMonth) {
      laterThisMonth.push(r);
    } else {
      future.push(r);
    }
  }

  const byStartAsc = (
    a: CompanyEventRegistrationItem,
    b: CompanyEventRegistrationItem,
  ) => (a.startDate ?? "").localeCompare(b.startDate ?? "");
  const byStartDesc = (
    a: CompanyEventRegistrationItem,
    b: CompanyEventRegistrationItem,
  ) => (b.startDate ?? "").localeCompare(a.startDate ?? "");

  thisWeek.sort(byStartAsc);
  laterThisMonth.sort(byStartAsc);
  future.sort(byStartAsc);
  past.sort(byStartDesc);

  return { thisWeek, laterThisMonth, future, past };
}

export type CompanyFilters = {
  search: string;
  eventUuid: string;
  status: "all" | "active" | "cancelled";
};

const initialFilters: CompanyFilters = {
  search: "",
  eventUuid: "",
  status: "all",
};

function applyFilters(
  items: CompanyEventRegistrationItem[],
  filters: CompanyFilters,
): CompanyEventRegistrationItem[] {
  const needle = filters.search.trim().toLowerCase();
  return items
    .map((item) => {
      // Filter the enrollee list per-item; drop items with no surviving enrollees.
      const enrollments = item.enrollments.filter((e) => {
        if (filters.status === "active" && (e.isCancelled || e.isRefunded)) {
          return false;
        }
        if (filters.status === "cancelled" && !e.isCancelled && !e.isRefunded) {
          return false;
        }
        if (!needle) return true;
        const haystack = [
          e.firstName,
          e.lastName,
          e.email ?? "",
          item.eventName,
          item.sessionName,
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(needle);
      });

      return { ...item, enrollments };
    })
    .filter((item) => {
      if (filters.eventUuid && item.eventUuid !== filters.eventUuid) {
        return false;
      }
      return item.enrollments.length > 0;
    });
}

export function useCompanyEventRegistrations() {
  const [registrations, setRegistrations] = useState<
    CompanyEventRegistrationItem[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CompanyFilters>(initialFilters);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listCompanyEventRegistrations();
      setRegistrations(response.data.registrations);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Failed to load company event registrations.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const filtered = useMemo(
    () => applyFilters(registrations, filters),
    [registrations, filters],
  );

  const groups = useMemo(() => groupByTimeHorizon(filtered), [filtered]);

  const eventOptions = useMemo(() => {
    const seen = new Map<string, string>();
    registrations.forEach((r) => {
      if (r.eventUuid && !seen.has(r.eventUuid)) {
        seen.set(r.eventUuid, r.eventName);
      }
    });
    return Array.from(seen.entries()).map(([uuid, name]) => ({ uuid, name }));
  }, [registrations]);

  const upcomingCount =
    groups.thisWeek.length +
    groups.laterThisMonth.length +
    groups.future.length;

  return {
    groups,
    upcomingCount,
    pastCount: groups.past.length,
    eventOptions,
    filters,
    setFilters,
    loading,
    error,
    refetch: fetch,
  };
}
